import { useCallback, useEffect, useRef, useState } from "react";
import type { PosterArItem, PosterArManifest } from "@studio/shared";
import {
  OVERLAY_VIDEO_STYLE,
  buildSceneSpec,
  inAppBrowserName,
  isInAppBrowser,
  loadArEngine,
  parseTrackingTuning,
  probeCamera,
} from "../lib/arScene";

type Phase = "loading" | "closed" | "in-app" | "failed" | "ready" | "list";

/** 화면 주소 → 매니페스트 주소. `p` = 학생 한 장(작품 id) · `c` = 그 반 전체(수업 id). */
export function posterManifestUrl(kind: "p" | "c", id: string): string {
  return `/api/${kind}/${encodeURIComponent(id.toLowerCase())}/manifest`;
}

/** 인식 파일을 받는 동안 매니페스트를 다시 받는 횟수(409 = 그사이 누가 다시 확정함 · D8) */
export const MARKER_RETRIES = 2;

class MarkerFetchError extends Error {
  constructor(readonly status: number) {
    super(`marker fetch ${status}`);
    this.name = "MarkerFetchError";
  }
}

/** 인식 파일이 404 인데 반 화면이 닫혔는지조차 확인하지 못했다 — 목록(학생 이름·그림)을 보여 주지 않는다. */
export class MarkerGoneError extends Error {
  constructor() {
    super("marker gone");
    this.name = "MarkerGoneError";
  }
}

/**
 * 인식 파일을 화면이 직접 받는다(PLAN-poster-class-qr D8). MindAR 은 HTTP 오류를 모른다 —
 * 409·404 면 매니페스트부터 다시 받고(최대 MARKER_RETRIES 번), 받은 바이트는 Blob 주소로 엔진에 넘긴다.
 * `reload` 가 null 이면(반 화면이 닫힘·지운 수업) 그대로 멈춘다 — 안내는 reload 가 이미 띄웠다.
 */
export async function fetchMarker(
  manifest: PosterArManifest,
  reload: () => Promise<PosterArManifest | null>,
  get: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<{ manifest: PosterArManifest; bytes: Blob } | null> {
  let current = manifest;
  for (let attempt = 0; ; attempt += 1) {
    const response = await get(current.marker_url);
    if (response.ok) return { manifest: current, bytes: await response.blob() };
    // 🔑 404 = 그사이 교사가 반 화면을 닫았거나 수업이 지워졌다. 재시도 횟수와 상관없이 매니페스트를 다시 받아
    //    그 안내를 띄운다 — 던지면 화면이 옛 매니페스트로 «카메라 없이 보기» 목록을 보여 줘 닫힌 반이 보인다
    //    (Codex 검수 · 재검수). 다시 받기마저 실패하면 MarkerGoneError — 화면은 목록 대신 실패 안내를 띄운다.
    if (response.status === 404) {
      const fresh = await reload().catch(() => { throw new MarkerGoneError(); });
      if (!fresh) return null;
      if (attempt >= MARKER_RETRIES) throw new MarkerGoneError();
      current = fresh;
      continue;
    }
    if (response.status !== 409 || attempt >= MARKER_RETRIES) throw new MarkerFetchError(response.status);
    const fresh = await reload();
    if (!fresh) return null;
    current = fresh;
  }
}

/**
 * 포스터 AR 스캔 — **로그인 없음.**
 *   `/p/:workId`    포스터 QR 이 그 학생 포스터 한 장 AR 로 연다(2026-10-04 · 반 QR 이전 수업과 교사 시연작).
 *   `/c/:sessionId` 새 포스터 수업의 QR 이 그 반 전체 포스터 AR 로 연다(2026-10-14 · PLAN-poster-class-qr).
 *
 * 🔑 두 화면은 매니페스트 주소만 다르다. 반 화면 인식 파일은 서버가 이어 붙인 합본이고, 목록 순서 = 합본 순서다.
 *    카메라를 옆 포스터로 옮기면 그 학생의 그림·영상과 노래로 바뀐다.
 * 🔑 키링과 같은 «화면 가운데 고정» 오버레이(떨림 0) — MindAR 은 «포스터를 보고 있나» 감지기로만 쓴다.
 * 🔑 카메라가 안 되는 폰은 «카메라 없이 보기».
 * 🚨 키링 `/k/` 화면·코드는 건드리지 않는다(이미 인쇄되어 나간 QR).
 */
export function PosterScanPage({ manifestUrl }: { manifestUrl: string }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [manifest, setManifest] = useState<PosterArManifest | null>(null);
  const [markerSrc, setMarkerSrc] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [current, setCurrent] = useState<PosterArItem | null>(null);
  const [muted, setMuted] = useState(true);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mediaRef = useRef<HTMLVideoElement | HTMLImageElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async (): Promise<PosterArManifest | null> => {
    const response = await fetch(manifestUrl);
    const body = await response.json().catch(() => null) as PosterArManifest | { error?: { code?: string; message?: string } } | null;
    if (!response.ok) {
      const error = body && "error" in body ? body.error : undefined;
      setMessage(error?.message ?? "포스터를 열 수 없어요.");
      setPhase(response.status === 404 ? "closed" : "failed");
      return null;
    }
    return body as PosterArManifest;
  }, [manifestUrl]);

  /** 인식 파일까지 받아 화면에 올린다. 실패하면 «카메라 없이 보기» 목록으로. */
  const prepareMarker = useCallback(async (from: PosterArManifest): Promise<boolean> => {
    try {
      const fetched = await fetchMarker(from, load);
      if (!fetched) return false;
      setManifest(fetched.manifest);
      setMarkerSrc(URL.createObjectURL(fetched.bytes));
      return true;
    } catch (error) {
      console.error("[poster-scan] marker", error);
      if (error instanceof MarkerGoneError) {
        setMessage("연결이 불안정해요. 잠시 뒤 다시 열어 주세요.");
        setPhase("failed");
        return false;
      }
      setMessage("포스터 인식 파일을 받지 못해서 목록으로 보여 드려요.");
      setPhase("list");
      return false;
    }
  }, [load]);

  // 바뀌거나 화면을 떠나면 옛 Blob 주소를 놓는다.
  useEffect(() => () => {
    if (markerSrc) URL.revokeObjectURL(markerSrc);
  }, [markerSrc]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await load();
        if (cancelled || !loaded) return;
        setManifest(loaded);
        if (isInAppBrowser(navigator.userAgent)) {
          setPhase("in-app");
          return;
        }
        const camera = await probeCamera(navigator.mediaDevices);
        if (cancelled) return;
        if (camera !== "ok") {
          setMessage(camera === "denied" ? "카메라를 쓸 수 없어서 목록으로 보여 드려요." : "이 기기에서는 카메라를 열 수 없어서 목록으로 보여 드려요.");
          setPhase("list");
          return;
        }
        const [ready] = await Promise.all([prepareMarker(loaded), loadArEngine()]);
        if (!cancelled && ready) setPhase("ready");
      } catch {
        if (cancelled) return;
        setMessage("연결이 불안정해요. 잠시 뒤 다시 열어 주세요.");
        setPhase("failed");
      }
    })();
    return () => { cancelled = true; };
  }, [load, prepareMarker]);

  useEffect(() => {
    if (phase !== "ready" || !manifest || !markerSrc || !hostRef.current) return;
    const tuning = parseTrackingTuning(window.location.search);
    const spec = buildSceneSpec({ markerUrl: markerSrc, videoUrl: "" }, tuning);
    const scene = document.createElement("a-scene");
    // 한 장만 따라간다.
    for (const [name, value] of Object.entries(spec.scene)) {
      scene.setAttribute(name, name === "mindar-image" ? `${value} maxTrack: 1;` : value);
    }
    const entities = manifest.items.map((item) => {
      const entity = document.createElement("a-entity");
      entity.setAttribute("mindar-image-target", `targetIndex: ${item.index}`);
      scene.appendChild(entity);
      return entity;
    });
    scene.appendChild(document.createElement("a-camera"));
    const handlers = entities.map((entity, index) => {
      const item = manifest.items[index]!;
      const onFound = (): void => setCurrent(item);
      const onLost = (): void => setCurrent((value) => value?.index === item.index ? null : value);
      entity.addEventListener("targetFound", onFound);
      entity.addEventListener("targetLost", onLost);
      return { entity, onFound, onLost };
    });
    hostRef.current.appendChild(scene);
    // 엔진이 인식 파일을 못 읽었다 — 그사이 바뀌었으면 매니페스트·인식 파일부터 다시, 같으면 목록으로.
    const onError = (): void => {
      void load().then((fresh) => {
        if (fresh && fresh.etag !== manifest.etag) {
          void prepareMarker(fresh);
          return;
        }
        if (fresh) {
          setMessage("AR 을 열지 못해서 목록으로 보여 드려요.");
          setPhase("list");
        }
      });
    };
    scene.addEventListener("arError", onError);
    return () => {
      scene.removeEventListener("arError", onError);
      for (const { entity, onFound, onLost } of handlers) {
        entity.removeEventListener("targetFound", onFound);
        entity.removeEventListener("targetLost", onLost);
      }
      scene.remove();
    };
  }, [load, manifest, markerSrc, phase, prepareMarker]);

  // 지금 비추는 포스터의 영상·그림·노래
  useEffect(() => {
    const audio = audioRef.current;
    const media = mediaRef.current;
    if (!current) {
      audio?.pause();
      if (media instanceof HTMLVideoElement) media.pause();
      return;
    }
    if (media instanceof HTMLVideoElement) {
      media.muted = current.music ? true : muted;
      void media.play().catch(() => undefined);
    }
    if (audio && current.music) {
      audio.muted = muted;
      audio.currentTime = 0;
      void audio.play().catch(() => undefined);
    }
  }, [current, muted]);

  const overlayStyle = Object.fromEntries(Object.entries(OVERLAY_VIDEO_STYLE).filter(([key]) => key !== "aspectRatio"));

  const isClass = manifest?.scope === "class" || manifestUrl.startsWith("/api/c/");
  if (phase === "loading") return <ExhibitMessage title={isClass ? "우리 반 포스터 AR 을 여는 중이에요" : "포스터 AR 을 여는 중이에요"} body="잠시만 기다려 주세요." />;
  if (phase === "closed") return <ExhibitMessage title="지금은 볼 수 없어요" body={message} />;
  if (phase === "failed") return <ExhibitMessage title="지금은 열 수 없어요" body={message} />;
  if (phase === "in-app") {
    const name = inAppBrowserName(navigator.userAgent) ?? "이 앱";
    return (
      <ExhibitMessage title="다른 브라우저로 열어 주세요" body={`${name} 안에서는 카메라를 쓸 수 없어요. 오른쪽 위 메뉴에서 «다른 브라우저로 열기» 를 눌러 주세요.`}>
        <button type="button" className="button-secondary" onClick={() => void navigator.clipboard?.writeText(window.location.href)}>주소 복사하기</button>
        <button type="button" className="button-primary" onClick={() => setPhase("list")}>카메라 없이 보기</button>
      </ExhibitMessage>
    );
  }
  if (phase === "list" && manifest) {
    return (
      <main className="exhibit-list">
        <h1 tabIndex={-1}>{manifest.title}</h1>
        {message ? <p>{message}</p> : null}
        <div className="exhibit-list-grid">
          {manifest.items.map((item) => (
            <article key={item.index} className="exhibit-list-item">
              {item.media.type === "video"
                ? <video src={item.media.url} controls playsInline preload="metadata" poster={item.image_url} />
                : <img src={item.image_url} alt={`${item.student}의 포스터 그림`} />}
              <b>{item.student}</b>
              {item.music ? (
                <>
                  <small>♪ {item.music.title}</small>
                  <audio src={item.music.url} controls preload="none" />
                </>
              ) : null}
            </article>
          ))}
        </div>
      </main>
    );
  }

  return (
    <div className="ar-scan-root">
      <div className="ar-scene-host" ref={hostRef} />
      <div className="exhibit-hud-top">
        {current ? `${current.student}` : manifest?.title ?? ""}
      </div>
      {current ? (
        current.media.type === "video" ? (
          <video
            key={current.index}
            ref={(node) => { mediaRef.current = node; }}
            className="ar-overlay-video"
            src={current.media.url}
            playsInline
            loop
            muted
            style={overlayStyle}
          />
        ) : (
          <img
            key={current.index}
            ref={(node) => { mediaRef.current = node; }}
            className="ar-overlay-video exhibit-overlay-image"
            src={current.media.url}
            alt=""
            style={overlayStyle}
          />
        )
      ) : null}
      {current?.music ? <audio key={`a${current.index}`} ref={audioRef} src={current.music.url} loop /> : null}
      <div className="ar-scan-hud">
        <p>{current?.music ? `♪ ${current.music.title} · ${current.student}` : current ? current.student : isClass ? "카메라를 켜고 아무 포스터나 비추세요" : "포스터를 화면에 꽉 차게 비춰 보세요"}</p>
        <div className="ar-scan-hud-actions">
          <button type="button" className="button-secondary" onClick={() => setMuted((value) => !value)}>{muted ? "소리 켜기" : "소리 끄기"}</button>
          <button type="button" className="button-secondary" onClick={() => setPhase("list")}>카메라 없이 보기</button>
        </div>
      </div>
    </div>
  );
}

function ExhibitMessage({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <main className="ar-scan-message">
      <h1 tabIndex={-1}>{title}</h1>
      <p>{body}</p>
      {children ? <div className="ar-scan-message-actions">{children}</div> : null}
    </main>
  );
}
